import { type FC, useContext, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BackArrowIcon from '@assets/img/icons/arrow-back.svg?react';
import { AnimatePresence } from 'framer-motion';

import { AppRoute } from '@/common/app/routes';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import {
  H1,
  H2,
  StringSmall,
  TextMain,
} from '@/components/Typography/Typography';
import { Button } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import { ChooseOffer } from './ChooseOffer/ChooseOffer';
import { ConfirmBuy } from './ConfirmBuy/ConfirmBuy';
import { BuyDicesContext } from './context';

import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';

const BuyDiceModal: FC = () => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [activeOfferIndex, setActiveOfferIndex] = useState(0);
  const [isChooseOfferStep, setIsChooseOfferStep] = useState(true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const paymentStatus = usePaymentStatus();
  const diceOffers = useAppSelector(
    (state) => state.user.extendedInfo?.dicePackages,
  );

  const nextStep = (): void => {
    setIsChooseOfferStep(false);
  };

  const prevStep = (): void => {
    setIsChooseOfferStep(true);
  };

  const toMoreOffers = (): void => {
    closeModal();
    navigate(AppRoute.SHOP);
  };

  useLayoutEffect(() => {
    dispatch(changePaymentStatus(undefined));
  }, []);

  return (
    <BuyDicesContext.Provider value={{ activeOfferIndex, setActiveOfferIndex }}>
      <div className="relative w-full  px-4 pt-5 pb-6 flex flex-col items-center">
        {paymentStatus === 'pending' ? (
          <PendingStatus />
        ) : paymentStatus === 'success' ? (
          <SuccessfullStatus>
            <div className="bg-white/10 rounded-[8px] relative p-[13px] opacity-[0.6] mt-8">
              <Picture
                alt="dice"
                img={dicePng}
                webp={diceWebp}
                className="size-28"
              />
              <StringSmall
                weight="bold"
                className="bg-black/30 absolute bottom-[4px] left-1/2 -translate-x-1/2 !text-[24px] px-[8px] py-[4px] rounded-[8px]"
              >
                {diceOffers?.[activeOfferIndex].diceCount}
              </StringSmall>
            </div>
          </SuccessfullStatus>
        ) : (
          <>
            <div className="w-full flex flex-col items-center relative z-[1]">
              <H1 weight="extrabold" className="mb-6">
                {t('Purchase')}
              </H1>
              <H2 weight="bold" className="mb-2">
                {t('Keep crafting your luck!')}
              </H2>
              <StringSmall className="!text-white/80 text-center mb-5 max-w-[17.125rem]">
                {t('Roll the dice, collect rewards, keep moving forward!')}
              </StringSmall>
            </div>
            <AnimatePresence mode="sync">
              {isChooseOfferStep ? (
                <ChooseOffer nextStep={nextStep} />
              ) : (
                <ConfirmBuy />
              )}
            </AnimatePresence>
            <Button
              onClick={isChooseOfferStep ? toMoreOffers : prevStep}
              variant="transparent"
              className="!h-12 !w-[12.8125rem] flex items-center justify-center"
            >
              {isChooseOfferStep ? (
                <TextMain weight="extrabold">{t('More offers')}</TextMain>
              ) : (
                <div className="flex items-center gap-2">
                  <BackArrowIcon className="w-6 h-6 shink-0" />
                  <TextMain weight="extrabold">{t('Back')}</TextMain>
                </div>
              )}
            </Button>
          </>
        )}
        {(paymentStatus === 'pending' || isChooseOfferStep) && (
          <Close
            onClick={() => closeModal()}
            imageVariant="modal"
            size="lg"
            className="w-16 h-16 shrink-0 mt-11"
          />
        )}
      </div>
    </BuyDicesContext.Provider>
  );
};

export { BuyDiceModal };
